import React, { useRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Stack,
    FormControlLabel,
    Switch,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    Divider,
    Alert,
    Tooltip,
    IconButton,
    AlertTitle,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    ButtonGroup,
    Slider,
    Select,
    MenuItem,
    InputLabel,
    Badge,
    Paper,
    Grid,
    Snackbar,
    Slide,
    Fade
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import {
    Code,
    Image,
    Youtube,
    Palette,
    Layout,
    Copy,
    HelpCircle,
    Phone,
    Mail,
    MapPin,
    Linkedin,
    Globe,
    MessageSquare,
    User,
    Contact,
    Building,
    Calendar,
    Download,
    Eye,
    Settings,
    Sparkles,
    Zap,
    Star,
    ChevronDown,
    ZoomIn,
    ZoomOut,
    Smartphone,
    Monitor,
    Tablet,
    RefreshCw,
    Save,
    Share,
    Wand2,
    Twitter,
    Instagram,
    Facebook,
    Github,
    Heart,
    Award,
    TrendingUp,
    CheckCircle,
    X,
    AlertCircle,
    Info
} from 'lucide-react';

const EmailSignatureGenerator = ({
    profileData = {
        name: "John Doe",
        title: "Senior Software Engineer",
        company: "Tech Solutions Inc.",
        email: "john.doe@techsolutions.com",
        phone: "+1 (555) 123-4567",
        companyUrl: "https://techsolutions.com",
        companyAddress: "123 Tech Street, San Francisco, CA 94105",
        linkedinUrl: "https://linkedin.com/in/johndoe",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
        companyLogo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=150&h=150&fit=crop",
        profileBaseUrl: "myprofile.com",
        bio: "Passionate software engineer with 8+ years of experience in building scalable web applications."
    },
    companySettings = {},
    getProfileSlug = () => "john-doe",
    isTrial = false,
    navigate = () => {},
    setSnackbar = () => {}
}) => {
    const signatureRef = useRef(null);
    const initialLoadComplete = useRef(false);

    // Local snackbar state
    const [localSnackbar, setLocalSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // UI State
    const [activeTab, setActiveTab] = useState(0);
    const [previewScale, setPreviewScale] = useState(1);
    const [previewDevice, setPreviewDevice] = useState('desktop'); // desktop, mobile, tablet
    const [showGridlines, setShowGridlines] = useState(false);
    const [livePreview, setLivePreview] = useState(true);
    const [expandedSections, setExpandedSections] = useState(['basic', 'design']);

    // Storage settings
    const [useStorage, setUseStorage] = useState(true);
    const [settingsLoaded, setSettingsLoaded] = useState(false);

    // Company feature settings
    const aiSignatureEnabled = companySettings.enableAiSignature !== false;
    const signatureTemplatesEnabled = companySettings.enableSignatureTemplates !== false;

    // Enhanced Signature States
    const [signatureTheme, setSignatureTheme] = useState('modern');
    const [signatureColor, setSignatureColor] = useState('#2563eb');
    const [signatureGradient, setSignatureGradient] = useState(false);
    const [signatureLayout, setSignatureLayout] = useState('horizontal');
    const [fontFamily, setFontFamily] = useState('Inter');
    const [fontSize, setFontSize] = useState(14);
    const [borderRadius, setBorderRadius] = useState(8);
    const [padding, setPadding] = useState(16);
    const [shadowIntensity, setShadowIntensity] = useState(0);
    
    // Content States
    const [includePhoto, setIncludePhoto] = useState(true);
    const [photoType, setPhotoType] = useState('profile');
    const [photoSize, setPhotoSize] = useState(80);
    const [photoShape, setPhotoShape] = useState('circle'); // circle, square, rounded
    
    const [includeQR, setIncludeQR] = useState(true);
    const [includeQRPhoto, setIncludeQRPhoto] = useState(true);
    const [qrPhotoType, setQRPhotoType] = useState('profile');
    const [qrCodeType, setQrCodeType] = useState('contact');
    const [qrSize, setQrSize] = useState(70);
    
    // Social Media
    const [includeLinkedin, setIncludeLinkedin] = useState(true);
    const [linkedinUrl, setLinkedinUrl] = useState(profileData?.linkedinUrl || 'https://linkedin.com/in/yourprofile');
    // useState only reads profileData on first render; pick up the LinkedIn URL once the profile loads or changes
    useEffect(() => {
        if (profileData?.linkedinUrl) setLinkedinUrl(profileData.linkedinUrl);
    }, [profileData?.linkedinUrl]);
    const [includeTwitter, setIncludeTwitter] = useState(false);
    const [twitterUrl, setTwitterUrl] = useState('https://twitter.com/yourprofile');
    const [includeInstagram, setIncludeInstagram] = useState(false);
    const [instagramUrl, setInstagramUrl] = useState('https://instagram.com/yourprofile');
    const [includeGithub, setIncludeGithub] = useState(false);
    const [githubUrl, setGithubUrl] = useState('https://github.com/yourprofile');
    const [includeYouTube, setIncludeYouTube] = useState(false);
    const [youtubeUrl, setYoutubeUrl] = useState('https://youtube.com/@yourprofile');
    
    // Advanced Features
    const [includeBanner, setIncludeBanner] = useState(false);
    const [bannerText, setBannerText] = useState('🎉 Join us at TechConf 2024!');
    const [bannerUrl, setBannerUrl] = useState('');
    const [includeDisclaimer, setIncludeDisclaimer] = useState(false);
    const [disclaimerText, setDisclaimerText] = useState('This email is confidential and intended solely for the use of the individual to whom it is addressed.');
    
    const [companyLogoUrl, setCompanyLogoUrl] = useState(profileData?.companyLogo || 'https://example.com/company-logo.png');
    const [includeMeetingScheduler, setIncludeMeetingScheduler] = useState(false);
    const [meetingSchedulerUrl, setMeetingSchedulerUrl] = useState('');
    const [meetingButtonStyle, setMeetingButtonStyle] = useState('button'); // button, link, badge

    // Personal Info
    const [showPronouns, setShowPronouns] = useState(true);
    const [pronounChoice, setPronounChoice] = useState('he/him');
    const [customPronoun, setCustomPronoun] = useState('');
    const [showNickname, setShowNickname] = useState(false);
    const [nickname, setNickname] = useState('');
    const [includeTagline, setIncludeTagline] = useState(false);
    const [tagline, setTagline] = useState('Turning ideas into digital reality');

    // AI and Analytics
    const [aiSuggestions, setAiSuggestions] = useState(false);
    const [templateGalleryOpen, setTemplateGalleryOpen] = useState(false);
    const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

    // Color palette options
    const colorPalettes = [
        { name: 'Professional Blue', colors: ['#2563eb', '#1d4ed8', '#1e40af'] },
        { name: 'Success Green', colors: ['#10b981', '#059669', '#047857'] },
        { name: 'Creative Purple', colors: ['#8b5cf6', '#7c3aed', '#6d28d9'] },
        { name: 'Energy Orange', colors: ['#f59e0b', '#d97706', '#b45309'] },
        { name: 'Elegant Gray', colors: ['#6b7280', '#4b5563', '#374151'] },
        { name: 'Bold Red', colors: ['#ef4444', '#dc2626', '#b91c1c'] },
        { name: 'Ocean Teal', colors: ['#14b8a6', '#0d9488', '#0f766e'] },
        { name: 'Sunset Pink', colors: ['#ec4899', '#db2777', '#be185d'] }
    ];

    // Font options
    const fontOptions = [
        { name: 'Inter', value: 'Inter, sans-serif' },
        { name: 'Roboto', value: 'Roboto, sans-serif' },
        { name: 'Open Sans', value: 'Open Sans, sans-serif' },
        { name: 'Lato', value: 'Lato, sans-serif' },
        { name: 'Montserrat', value: 'Montserrat, sans-serif' },
        { name: 'Poppins', value: 'Poppins, sans-serif' },
        { name: 'Times New Roman', value: 'Times New Roman, serif' },
        { name: 'Georgia', value: 'Georgia, serif' },
        { name: 'Arial', value: 'Arial, sans-serif' },
        { name: 'Helvetica', value: 'Helvetica, sans-serif' }
    ];

    // Generate vCard data
    const generateVCardData = () => {
        const nameParts = profileData.name.split(' ');
        const lastName = nameParts.length > 1 ? nameParts.pop() : '';
        const firstName = nameParts.join(' ');

        let vCardData = 'BEGIN:VCARD\nVERSION:3.0\n';
        vCardData += `FN:${profileData.name}\n`;
        vCardData += `N:${lastName};${firstName};;;\n`;
        if (profileData.title) vCardData += `TITLE:${profileData.title}\n`;
        if (profileData.company) vCardData += `ORG:${profileData.company}\n`;
        if (profileData.email) vCardData += `EMAIL;type=WORK:${profileData.email}\n`;
        if (profileData.phone) vCardData += `TEL;type=CELL:${profileData.phone}\n`;
        if (profileData.companyAddress) vCardData += `ADR;type=WORK:;;${profileData.companyAddress}\n`;
        if (profileData.companyUrl) vCardData += `URL;type=WORK:${profileData.companyUrl}\n`;
        if (profileData.linkedinUrl) vCardData += `URL;type=LINKEDIN:${profileData.linkedinUrl}\n`;
        if (profileData.bio) {
            const formattedBio = profileData.bio
                .replace(/\r\n|\r|\n/g, '\\n')
                .replace(/,/g, '\\,')
                .replace(/;/g, '\\;')
                .replace(/\\/g, '\\\\');
            vCardData += `NOTE:${formattedBio}\n`;
        }
        vCardData += 'END:VCARD';
        return vCardData;
    };

    // Get QR code value
    const getQRCodeValue = () => {
        if (qrCodeType === 'contact') {
            return generateVCardData();
        } else {
            return `https://${profileData.profileBaseUrl}/profile/${getProfileSlug()}`;
        }
    };

    // Get photo source
    const getPhotoSource = (type) => {
        return type === 'profile' ? profileData.avatar : companyLogoUrl;
    };

    // Handle color selection
    const handleColorSelect = (color) => {
        setSignatureColor(color);
    };

    // Handle template application
    const applyTemplateWithNotification = (template) => {
        applyTemplate(template);
    };

    // Predefined templates
    const templates = [
        {
            name: 'Corporate Professional',
            theme: 'classic',
            color: '#1e40af',
            layout: 'horizontal',
            fontFamily: 'Times New Roman',
            borderRadius: 0
        },
        {
            name: 'Modern Minimal',
            theme: 'minimal',
            color: '#6b7280',
            layout: 'vertical',
            fontFamily: 'Inter',
            borderRadius: 12
        },
        {
            name: 'Creative Bold',
            theme: 'modern',
            color: '#ec4899',
            layout: 'horizontal',
            fontFamily: 'Poppins',
            borderRadius: 16
        },
        {
            name: 'Tech Startup',
            theme: 'modern',
            color: '#8b5cf6',
            layout: 'horizontal',
            fontFamily: 'Inter',
            borderRadius: 8
        }
    ];

    // Enhanced signature styles
    const getSignatureStyles = () => {
        const baseFont = fontOptions.find(f => f.name === fontFamily)?.value || 'Inter, sans-serif';
        const scaledFontSize = fontSize * previewScale;
        const scaledPadding = padding * previewScale;
        const scaledBorderRadius = borderRadius * previewScale;
        
        const gradientColor = signatureGradient 
            ? `linear-gradient(135deg, ${signatureColor}, ${signatureColor}99)`
            : signatureColor;

        const themes = {
            modern: {
                container: {
                    fontFamily: baseFont,
                    borderLeft: signatureGradient 
                        ? `3px solid ${signatureColor}` 
                        : `3px solid ${signatureColor}`,
                    padding: `0 0 0 ${scaledPadding}px`,
                    maxWidth: signatureLayout === 'horizontal' ? '600px' : '300px',
                    display: 'flex',
                    flexDirection: signatureLayout === 'horizontal' ? 'row' : 'column',
                    alignItems: signatureLayout === 'horizontal' ? 'center' : 'flex-start',
                    gap: `${scaledPadding}px`,
                    backgroundColor: '#ffffff',
                    borderRadius: `${scaledBorderRadius}px`,
                    boxShadow: shadowIntensity > 0 ? `0 ${shadowIntensity * 2}px ${shadowIntensity * 8}px rgba(0,0,0,0.1)` : 'none',
                    transition: 'all 0.3s ease'
                },
                name: {
                    fontSize: `${scaledFontSize + 4}px`,
                    fontWeight: 'bold',
                    color: signatureColor,
                    margin: '0 0 2px 0',
                    lineHeight: 1.2
                },
                title: {
                    fontSize: `${scaledFontSize}px`,
                    color: '#666666',
                    margin: '0 0 8px 0',
                    lineHeight: 1.3
                },
                company: {
                    fontSize: `${scaledFontSize}px`,
                    fontWeight: 'bold',
                    margin: '0 0 12px 0',
                    lineHeight: 1.3
                },
                contact: {
                    fontSize: `${scaledFontSize - 1}px`,
                    margin: '3px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    lineHeight: 1.4
                },
                avatarContainer: {
                    marginRight: signatureLayout === 'horizontal' ? `${scaledPadding}px` : '0',
                    marginBottom: signatureLayout === 'horizontal' ? '0' : `${scaledPadding}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                },
                avatar: {
                    width: `${photoSize * previewScale}px`,
                    height: `${photoSize * previewScale}px`,
                    borderRadius: photoShape === 'circle' ? '50%' : 
                                 photoShape === 'rounded' ? `${scaledBorderRadius}px` : '0',
                    border: `2px solid ${signatureColor}`,
                    objectFit: 'cover',
                    transition: 'all 0.3s ease'
                },
                qrCode: {
                    display: includeQR ? 'block' : 'none',
                    width: `${qrSize * previewScale}px`,
                    height: `${qrSize * previewScale}px`
                },
                icon: {
                    width: '20px',
                    height: '20px',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: signatureColor,
                    borderRadius: '50%',
                    padding: '4px',
                    transition: 'all 0.3s ease'
                },
                socialContainer: {
                    display: 'flex',
                    gap: '8px',
                    marginTop: '8px',
                    flexWrap: 'wrap'
                },
                socialIcon: {
                    width: '24px',
                    height: '24px',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: signatureColor,
                    borderRadius: '50%',
                    padding: '4px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'scale(1.1)'
                    }
                }
            },
            // Add classic and minimal themes with similar enhancements...
            classic: {
                // Similar structure but with classic styling
                container: {
                    fontFamily: baseFont,
                    maxWidth: signatureLayout === 'horizontal' ? '600px' : '300px',
                    display: 'flex',
                    flexDirection: signatureLayout === 'horizontal' ? 'row' : 'column',
                    alignItems: signatureLayout === 'horizontal' ? 'center' : 'flex-start',
                    gap: `${scaledPadding}px`,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    padding: `${scaledPadding}px`,
                    borderRadius: `${scaledBorderRadius}px`
                },
                name: {
                    fontSize: `${scaledFontSize + 4}px`,
                    fontWeight: 'bold',
                    color: '#000000',
                    margin: '0 0 2px 0'
                },
                title: {
                    fontSize: `${scaledFontSize}px`,
                    fontStyle: 'italic',
                    color: '#444444',
                    margin: '0 0 8px 0'
                },
                company: {
                    fontSize: `${scaledFontSize}px`,
                    fontWeight: 'bold',
                    margin: '0 0 12px 0'
                },
                contact: {
                    fontSize: `${scaledFontSize - 1}px`,
                    margin: '3px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                },
                avatarContainer: {
                    marginRight: signatureLayout === 'horizontal' ? `${scaledPadding}px` : '0',
                    marginBottom: signatureLayout === 'horizontal' ? '0' : `${scaledPadding}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                },
                avatar: {
                    width: `${photoSize * previewScale}px`,
                    height: `${photoSize * previewScale}px`,
                    borderRadius: photoShape === 'circle' ? '50%' : 
                                 photoShape === 'rounded' ? `${scaledBorderRadius}px` : '0',
                    border: '1px solid #000000',
                    objectFit: 'cover'
                },
                qrCode: {
                    display: includeQR ? 'block' : 'none',
                    width: `${qrSize * previewScale}px`,
                    height: `${qrSize * previewScale}px`
                },
                icon: {
                    width: '20px',
                    height: '20px',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '4px'
                },
                socialContainer: {
                    display: 'flex',
                    gap: '8px',
                    marginTop: '8px'
                },
                socialIcon: {
                    width: '24px',
                    height: '24px',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '4px'
                }
            },
            minimal: {
                container: {
                    fontFamily: baseFont,
                    maxWidth: signatureLayout === 'horizontal' ? '600px' : '300px',
                    display: 'flex',
                    flexDirection: signatureLayout === 'horizontal' ? 'row' : 'column',
                    alignItems: signatureLayout === 'horizontal' ? 'center' : 'flex-start',
                    gap: `${scaledPadding}px`,
                    backgroundColor: '#ffffff',
                    padding: `${scaledPadding}px`
                },
                name: {
                    fontSize: `${scaledFontSize + 2}px`,
                    fontWeight: 'bold',
                    color: '#000000',
                    margin: '0 0 2px 0'
                },
                title: {
                    fontSize: `${scaledFontSize - 1}px`,
                    color: '#666666',
                    margin: '0 0 8px 0'
                },
                company: {
                    fontSize: `${scaledFontSize - 1}px`,
                    fontWeight: 'bold',
                    margin: '0 0 12px 0'
                },
                contact: {
                    fontSize: `${scaledFontSize - 2}px`,
                    margin: '2px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                },
                avatarContainer: {
                    marginRight: signatureLayout === 'horizontal' ? `${scaledPadding}px` : '0',
                    marginBottom: signatureLayout === 'horizontal' ? '0' : `${scaledPadding}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                },
                avatar: {
                    width: `${(photoSize - 10) * previewScale}px`,
                    height: `${(photoSize - 10) * previewScale}px`,
                    borderRadius: photoShape === 'circle' ? '50%' : 
                                 photoShape === 'rounded' ? `${scaledBorderRadius}px` : '0',
                    objectFit: 'cover'
                },
                qrCode: {
                    display: includeQR ? 'block' : 'none',
                    width: `${(qrSize - 10) * previewScale}px`,
                    height: `${(qrSize - 10) * previewScale}px`
                },
                icon: {
                    width: '16px',
                    height: '16px',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '2px'
                },
                socialContainer: {
                    display: 'flex',
                    gap: '6px',
                    marginTop: '6px'
                },
                socialIcon: {
                    width: '20px',
                    height: '20px',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '2px'
                }
            }
        };

        return themes[signatureTheme];
    };

    // Handle section toggle
    const toggleSection = (section) => {
        setExpandedSections(prev => 
            prev.includes(section) 
                ? prev.filter(s => s !== section)
                : [...prev, section]
        );
    };

    // Handle snackbar close
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setLocalSnackbar({ ...localSnackbar, open: false });
    };

    // Copy signature HTML (raw code)
    const copySignatureHTML = () => {
        try {
            if (!signatureRef.current) {
                throw new Error('Signature element not found');
            }

            const signatureHTML = signatureRef.current.innerHTML;
            navigator.clipboard.writeText(signatureHTML);

            setLocalSnackbar({
                open: true,
                message: '🔧 HTML Code Copied! Raw HTML has been copied to your clipboard for custom implementations.',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error copying signature HTML:', error);
            setLocalSnackbar({
                open: true,
                message: '❌ Failed to copy HTML code. Please try again.',
                severity: 'error'
            });
        }
    };

    // Convert SVG to data URL image with standardized sizing
    const svgToDataURL = (svgElement) => {
        return new Promise((resolve) => {
            try {
                const svgData = new XMLSerializer().serializeToString(svgElement);
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                
                // Standardized sizes for different types of icons
                const rect = svgElement.getBoundingClientRect();
                let standardWidth, standardHeight;
                
                // Better QR code detection - check if it's a QRCodeSVG component
                const isQRCode = svgElement.querySelector('path') && (rect.width > 40 || rect.height > 40) || 
                                svgElement.closest('[style*="70px"]') || 
                                svgElement.parentElement?.textContent?.includes('Scan') ||
                                svgData.includes('QR') ||
                                rect.width === rect.height && rect.width > 50;
                
                if (isQRCode) {
                    // QR Code - larger size
                    standardWidth = Math.max(70, rect.width);
                    standardHeight = Math.max(70, rect.height);
                } else {
                    // Contact icons - standard small size
                    standardWidth = 24;
                    standardHeight = 24;
                }
                
                canvas.width = standardWidth * 2; // Higher resolution
                canvas.height = standardHeight * 2;
                
                img.onload = function() {
                    // Clear canvas with transparent background
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    const dataURL = canvas.toDataURL('image/png');
                    resolve({
                        dataURL,
                        width: standardWidth,
                        height: standardHeight,
                        isQR: isQRCode
                    });
                };
                
                img.onerror = function() {
                    resolve(null);
                };
                
                const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(svgBlob);
                img.src = url;
            } catch (error) {
                console.error('Error converting SVG:', error);
                resolve(null);
            }
        });
    };

    // Copy signature as rich content with table structure and hidden outlines
    const copySignatureRichContent = async () => {
        try {
            if (!signatureRef.current) {
                throw new Error('Signature element not found');
            }

            // Show loading state
            setLocalSnackbar({
                open: true,
                message: '⏳ Converting icons and creating horizontal clickable layout...',
                severity: 'info'
            });

            // Small delay to ensure preview is fully rendered
            await new Promise(resolve => setTimeout(resolve, 100));

            // Clone the signature to avoid modifying the original
            const signatureClone = signatureRef.current.cloneNode(true);
            
            // Create temporary container
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '-9999px';
            tempContainer.appendChild(signatureClone);
            document.body.appendChild(tempContainer);

            // Find all SVG elements and convert them
            const svgElements = Array.from(tempContainer.querySelectorAll('svg'));
            const originalSvgElements = Array.from(signatureRef.current.querySelectorAll('svg'));
            
            // Store converted images with metadata
            const convertedImages = {
                qrCode: null,
                socialIcons: [],
                contactIcons: []
            };
            
            // Convert each SVG to an image with proper categorization
            for (let i = 0; i < svgElements.length; i++) {
                const svgElement = svgElements[i];
                const originalSvg = originalSvgElements[i];
                
                if (originalSvg) {
                    try {
                        // Convert SVG to data URL with standardized sizing
                        const imageData = await svgToDataURL(originalSvg);
                        
                        if (imageData) {
                            // Create an img element to replace the SVG
                            const imgElement = document.createElement('img');
                            imgElement.src = imageData.dataURL;
                            imgElement.style.width = `${imageData.width}px`;
                            imgElement.style.height = `${imageData.height}px`;
                            imgElement.style.display = 'inline-block';
                            imgElement.style.verticalAlign = 'middle';
                            imgElement.style.border = 'none';
                            imgElement.style.outline = 'none';
                            imgElement.style.textDecoration = 'none';
                            
                            // Determine the type of icon and store accordingly
                            if (imageData.isQR) {
                                convertedImages.qrCode = imageData.dataURL;
                                imgElement.alt = 'QR Code';
                            } else {
                                // Check if it's a social media icon based on context
                                const parentElement = svgElement.parentElement;
                                const grandParent = parentElement?.parentElement;
                                const containerText = (parentElement?.textContent || grandParent?.textContent || '').toLowerCase();
                                
                                // Check for social media icons by looking at nearby text or container
                                if (includeLinkedin && (containerText.includes('linkedin') || parentElement?.closest('a')?.href?.includes('linkedin'))) {
                                    convertedImages.socialIcons.push({ 
                                        type: 'linkedin', 
                                        url: linkedinUrl, 
                                        image: imageData.dataURL 
                                    });
                                } else if (includeTwitter && (containerText.includes('twitter') || parentElement?.closest('a')?.href?.includes('twitter'))) {
                                    convertedImages.socialIcons.push({ 
                                        type: 'twitter', 
                                        url: twitterUrl, 
                                        image: imageData.dataURL 
                                    });
                                } else if (includeInstagram && (containerText.includes('instagram') || parentElement?.closest('a')?.href?.includes('instagram'))) {
                                    convertedImages.socialIcons.push({ 
                                        type: 'instagram', 
                                        url: instagramUrl, 
                                        image: imageData.dataURL 
                                    });
                                } else if (includeGithub && (containerText.includes('github') || parentElement?.closest('a')?.href?.includes('github'))) {
                                    convertedImages.socialIcons.push({ 
                                        type: 'github', 
                                        url: githubUrl, 
                                        image: imageData.dataURL 
                                    });
                                } else if (includeYouTube && (containerText.includes('youtube') || parentElement?.closest('a')?.href?.includes('youtube'))) {
                                    convertedImages.socialIcons.push({ 
                                        type: 'youtube', 
                                        url: youtubeUrl, 
                                        image: imageData.dataURL 
                                    });
                                } else {
                                    // Contact icons (phone, email, website)
                                    convertedImages.contactIcons.push(imageData.dataURL);
                                }
                                imgElement.alt = 'Icon';
                            }

                            // Replace SVG with image
                            if (svgElement.parentNode) {
                                svgElement.parentNode.replaceChild(imgElement, svgElement);
                            }
                        }
                    } catch (conversionError) {
                        console.error('Error converting SVG to image:', conversionError);
                    }
                }
            }

            // Add social icons that might not have been detected via SVG
            // This ensures all enabled social icons are included
            const socialPlatforms = [
                { enabled: includeLinkedin, type: 'linkedin', url: linkedinUrl },
                { enabled: includeTwitter, type: 'twitter', url: twitterUrl },
                { enabled: includeInstagram, type: 'instagram', url: instagramUrl },
                { enabled: includeGithub, type: 'github', url: githubUrl },
                { enabled: includeYouTube, type: 'youtube', url: youtubeUrl }
            ];

            socialPlatforms.forEach(platform => {
                if (platform.enabled && !convertedImages.socialIcons.find(s => s.type === platform.type)) {
                    // Create a simple placeholder icon for missing social platforms
                    convertedImages.socialIcons.push({
                        type: platform.type,
                        url: platform.url,
                        image: null // Will use fallback text
                    });
                }
            });

            // Fallback QR code generation if not detected
            if (includeQR && !convertedImages.qrCode) {
                try {
                    // Create a QR code manually if not detected
                    const qrValue = getQRCodeValue();
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = qrSize * 2;
                    canvas.height = qrSize * 2;
                    
                    // Simple QR placeholder (you could integrate an actual QR library here)
                    ctx.fillStyle = '#000000';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.fillStyle = '#ffffff';
                    ctx.font = 'bold 16px Arial';
                    ctx.textAlign = 'center';
                    ctx.fillText('QR', canvas.width/2, canvas.height/2);
                    
                    convertedImages.qrCode = canvas.toDataURL('image/png');
                } catch (qrError) {
                    console.log('QR fallback generation failed:', qrError);
                }
            }

            // Extract content with proper categorization
            const extractContent = () => {
                const content = {
                    profileImage: null,
                    name: profileData.name,
                    title: profileData.title,
                    company: profileData.company,
                    phone: profileData.phone,
                    email: profileData.email,
                    website: profileData.companyUrl,
                    tagline: includeTagline ? tagline : '',
                    nickname: showNickname ? nickname : '',
                    pronouns: showPronouns ? (pronounChoice === 'custom' ? customPronoun : pronounChoice) : '',
                    qrCode: convertedImages.qrCode,
                    socialIcons: convertedImages.socialIcons,
                    contactIcons: convertedImages.contactIcons,
                    banner: includeBanner ? bannerText : '',
                    bannerUrl: includeBanner ? bannerUrl : '',
                    scheduler: includeMeetingScheduler ? meetingSchedulerUrl : '',
                    disclaimer: includeDisclaimer ? disclaimerText : ''
                };

                // Extract profile/company image
                const profileImages = tempContainer.querySelectorAll('img');
                profileImages.forEach(img => {
                    if (!img.src.startsWith('data:image/png')) {
                        // This is likely the profile photo or company logo
                        content.profileImage = img.src;
                    }
                });

                return content;
            };

            const content = extractContent();

            // Create enhanced table structure with clickable contact info
            const createTableStructure = () => {
                const borderColor = signatureColor;
                const fontFamilyValue = fontOptions.find(f => f.name === fontFamily)?.value || 'Arial, sans-serif';

                // Helper: Generate QR code as PNG data URL using qrcode library
                let qrDataUrl = '';
                if (includeQR && getQRCodeValue()) {
                    try {
                        // Synchronous QR code generation for email signatures
                        // eslint-disable-next-line
                        const QRCode = require('qrcode');
                        QRCode.toDataURL(getQRCodeValue(), { width: qrSize, margin: 0 }, (err, url) => {
                            if (!err) qrDataUrl = url;
                        });
                    } catch (e) {
                        qrDataUrl = content.qrCode || '';
                    }
                }

                // Social icon PNGs (replace with your own or use a CDN)
                const socialIconPngs = {
                    linkedin: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/linkedin.svg',
                    twitter: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/twitter.svg',
                    instagram: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/instagram.svg',
                    github: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/github.svg',
                    youtube: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/youtube.svg'
                };

                let html = `
<table cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse; border: none; outline: none; font-family: ${fontFamilyValue}; font-size: ${fontSize}px; line-height: 1.4; color: ${borderColor}; margin: 0; padding: 0; max-width: 600px;">
    <tbody>
        <tr style="border: none; outline: none;">
            <td style="width: 4px; background-color: ${borderColor}; border: none; outline: none; padding: 0; margin: 0; vertical-align: top;">
                <div style="width: 4px; height: 100%; background-color: ${borderColor}; border: none; outline: none;">&nbsp;</div>
            </td>
            <td style="padding: 0 0 0 ${padding}px; border: none; outline: none; vertical-align: top;">
                <table cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse; border: none; outline: none; width: 100%;">
                    <tbody>
                        <tr style="border: none; outline: none;">
                            <!-- First column: Profile Image and QR Code -->
                            <td style="vertical-align: top; padding-right: 16px; border: none; outline: none;">
                                <div style="display: flex; flex-direction: column; align-items: center; gap: 12px;">
                                    ${content.profileImage ? `
                                        <img 
                                            src="${content.profileImage}" 
                                            alt="Profile Photo" 
                                            style="width: ${photoSize}px; height: ${photoSize}px; border-radius: ${photoShape === 'circle' ? '50%' : photoShape === 'rounded' ? '12px' : '0'}; object-fit: cover; border: 2px solid ${borderColor}; display: block; margin-bottom: 8px;"
                                        />
                                    ` : ''}
                                    ${includeQR && (qrDataUrl || content.qrCode) ? `
                                        <div style="margin-top: 8px;">
                                            <img src="${qrDataUrl || content.qrCode}" width="${qrSize}" height="${qrSize}" alt="QR Code" style="display:block;" />
                                            ${qrCodeType === 'contact' ? `<div style="font-size: 10px; color: ${borderColor}; margin-top: 4px; text-align: center;">Scan for contact</div>` : ''}
                                        </div>
                                    ` : ''}
                                </div>
                            </td>
                            <!-- Second column: All other content -->
                            <td style="border: none; outline: none; color: ${borderColor}; vertical-align: top;">
                                <div style="margin-bottom: 4px; color: ${borderColor};">Regards,</div>
                                <div style="font-size: ${fontSize + 4}px; font-weight: bold; color: ${borderColor}; margin: 0 0 2px 0;">
                                    ${content.name}${content.nickname ? ` (${content.nickname})` : ''}${content.pronouns ? ` (${content.pronouns})` : ''}
                                </div>
                                <div style="font-size: ${fontSize}px; color: ${borderColor}; margin: 0 0 4px 0;">
                                    ${content.title}
                                </div>
                                <div style="font-size: ${fontSize}px; font-weight: bold; color: ${borderColor}; margin: 0 0 8px 0;">
                                    ${content.company}
                                </div>
                                ${content.tagline ? `
                                <div style="font-size: ${fontSize - 2}px; font-style: italic; color: ${borderColor}; margin: 4px 0 8px 0;">
                                    ${content.tagline}
                                </div>` : ''}
                                <div style="margin: 8px 0 0 0;">
                                    <a href="tel:${content.phone}" style="margin-right: 12px;">
                                        <img src="https://cdn.jsdelivr.net/gh/lucide-icons/lucide/icons/phone.svg" width="18" height="18" style="vertical-align: middle; filter: invert(40%) sepia(100%) saturate(500%) hue-rotate(180deg);" alt="Phone" />
                                    </a>
                                    <a href="mailto:${content.email}" style="margin-right: 12px;">
                                        <img src="https://cdn.jsdelivr.net/gh/lucide-icons/lucide/icons/mail.svg" width="18" height="18" style="vertical-align: middle; filter: invert(40%) sepia(100%) saturate(500%) hue-rotate(180deg);" alt="Email" />
                                    </a>
                                    <a href="${content.website}" target="_blank">
                                        <img src="https://cdn.jsdelivr.net/gh/lucide-icons/lucide/icons/globe.svg" width="18" height="18" style="vertical-align: middle; filter: invert(40%) sepia(100%) saturate(500%) hue-rotate(180deg);" alt="Website" />
                                    </a>
                                </div>
                                <div style="margin: 8px 0 0 0;">
                                    ${includeTwitter ? `<a href="${twitterUrl}" style="display:inline-block; margin-right:6px;"><img src="${socialIconPngs.twitter}" width="20" height="20" style="vertical-align:middle; filter: invert(40%) sepia(100%) saturate(500%) hue-rotate(180deg);" alt="Twitter" /></a>` : ''}
                                    ${includeInstagram ? `<a href="${instagramUrl}" style="display:inline-block; margin-right:6px;"><img src="${socialIconPngs.instagram}" width="20" height="20" style="vertical-align:middle; filter: invert(40%) sepia(100%) saturate(500%) hue-rotate(180deg);" alt="Instagram" /></a>` : ''}
                                    ${includeGithub ? `<a href="${githubUrl}" style="display:inline-block; margin-right:6px;"><img src="${socialIconPngs.github}" width="20" height="20" style="vertical-align:middle; filter: invert(40%) sepia(100%) saturate(500%) hue-rotate(180deg);" alt="GitHub" /></a>` : ''}
                                    ${includeYouTube ? `<a href="${youtubeUrl}" style="display:inline-block; margin-right:6px;"><img src="${socialIconPngs.youtube}" width="20" height="20" style="vertical-align:middle; filter: invert(40%) sepia(100%) saturate(500%) hue-rotate(180deg);" alt="YouTube" /></a>` : ''}
                                </div>
                                ${includeLinkedin && linkedinUrl ? `
                                <div style="margin: 6px 0 0 0; font-size: ${fontSize - 1}px;">
                                    <a href="${linkedinUrl}" target="_blank" style="color: ${signatureColor}; text-decoration: none;">${linkedinUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}</a>
                                </div>` : ''}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </td>
        </tr>
    </tbody>
</table>
                `;
                return html;
            };

            // Generate the table structure
            const tableHTML = createTableStructure();
            const textContent = tempContainer.innerText || tempContainer.textContent;

            // Clean up
            document.body.removeChild(tempContainer);

            // Try to copy using multiple methods for best compatibility
            let copySuccess = false;

            // Method 1: Try modern clipboard API with rich content
            if (navigator.clipboard && window.ClipboardItem) {
                try {
                    const clipboardItem = new ClipboardItem({
                        'text/html': new Blob([tableHTML], { type: 'text/html' }),
                        'text/plain': new Blob([textContent], { type: 'text/plain' })
                    });
                    
                    await navigator.clipboard.write([clipboardItem]);
                    copySuccess = true;
                } catch (clipboardError) {
                    console.log('Modern clipboard API failed, trying fallback');
                }
            }

            // Method 2: Fallback to text clipboard
            if (!copySuccess) {
                try {
                    await navigator.clipboard.writeText(tableHTML);
                    copySuccess = true;
                } catch (textError) {
                    console.log('Text clipboard failed, trying execCommand');
                }
            }

            // Method 3: Last resort - manual selection method
            if (!copySuccess) {
                try {
                    const tempElement = document.createElement('div');
                    tempElement.innerHTML = tableHTML;
                    tempElement.style.position = 'absolute';
                    tempElement.style.left = '-9999px';
                    document.body.appendChild(tempElement);

                    const range = document.createRange();
                    range.selectNodeContents(tempElement);
                    const selection = window.getSelection();
                    selection.removeAllRanges();
                    selection.addRange(range);

                    const execSuccess = document.execCommand('copy');
                    selection.removeAllRanges();
                    document.body.removeChild(tempElement);

                    if (execSuccess) copySuccess = true;
                } catch (execError) {
                    console.error('All copy methods failed');
                }
            }

            if (copySuccess) {
                const elementCount = {
                    profileImage: !!content.profileImage,
                    qrCode: !!content.qrCode,
                    socialLinks: content.socialIcons.length,
                    contactIcons: content.contactIcons.length,
                    hasScheduler: !!content.scheduler,
                    hasBanner: !!content.banner
                };

                let successMessage = '✅ Perfect Horizontal Layout! ';
                successMessage += 'Clean table structure with hidden outlines. ';
                if (elementCount.qrCode) successMessage += 'QR code converted and linked. ';
                else if (includeQR) successMessage += 'QR placeholder included (enable QR in settings). ';
                if (elementCount.profileImage) successMessage += 'Profile image included. ';
                if (elementCount.socialLinks > 0) successMessage += `${elementCount.socialLinks} social icon(s) in horizontal row. `;
                if (elementCount.contactIcons > 0) successMessage += `Contact icons in horizontal row (clickable). `;
                if (elementCount.hasScheduler) successMessage += 'Meeting scheduler button added. ';
                successMessage += 'Icons arranged horizontally for email! Paste with Ctrl+V.';

                setLocalSnackbar({
                    open: true,
                    message: successMessage,
                    severity: 'success'
                });
            } else {
                throw new Error('All copy methods failed');
            }

        } catch (error) {
            console.error('Error copying signature:', error);
            
            setLocalSnackbar({
                open: true,
                message: '❌ Copy Failed. Please try manually selecting the signature and copying with Ctrl+C. Some browsers have restrictions on automatic copying.',
                severity: 'error'
            });
        }
    };

    // Download signature as PNG image
    const downloadSignatureImage = async () => {
        try {
            setLocalSnackbar({
                open: true,
                message: '🎨 Generating high-quality PNG image...',
                severity: 'info'
            });

            if (!signatureRef.current) {
                throw new Error('Signature element not found');
            }

            // Wait for all images to load
            const images = signatureRef.current.querySelectorAll('img');
            const imagePromises = Array.from(images).map(img => {
                return new Promise((resolve) => {
                    if (img.complete) {
                        resolve();
                    } else {
                        img.onload = resolve;
                        img.onerror = resolve;
                    }
                });
            });

            await Promise.all(imagePromises);

            // Create a temporary container for the signature
            const tempContainer = document.createElement('div');
            tempContainer.style.position = 'absolute';
            tempContainer.style.left = '-9999px';
            tempContainer.style.top = '-9999px';
            tempContainer.style.background = '#ffffff';
            tempContainer.style.padding = '20px';
            
            // Clone the signature
            const signatureClone = signatureRef.current.cloneNode(true);
            tempContainer.appendChild(signatureClone);
            document.body.appendChild(tempContainer);

            // Load html2canvas dynamically
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
            
            const loadPromise = new Promise((resolve, reject) => {
                script.onload = () => resolve(window.html2canvas);
                script.onerror = reject;
                document.head.appendChild(script);
            });

            const html2canvas = await loadPromise;
            
            const canvas = await html2canvas(signatureClone, {
                scale: 2, // High resolution
                backgroundColor: '#ffffff',
                useCORS: true,
                allowTaint: true,
                logging: false,
                width: signatureClone.offsetWidth,
                height: signatureClone.offsetHeight
            });

            // Clean up
            document.body.removeChild(tempContainer);
            document.head.removeChild(script);

            // Create download link
            const imageUrl = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.href = imageUrl;
            downloadLink.download = `${profileData.name.toLowerCase().replace(/\s+/g, '-')}-signature.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);

            setLocalSnackbar({
                open: true,
                message: '📸 Signature Downloaded! High-quality PNG saved to your downloads folder.',
                severity: 'success'
            });

        } catch (error) {
            console.error('Error downloading signature:', error);
            setLocalSnackbar({
                open: true,
                message: '❌ Download Failed. Please try again or use a different browser.',
                severity: 'error'
            });
        }
    };

    // Share template
    const shareTemplate = () => {
        setLocalSnackbar({
            open: true,
            message: '🔗 Share Link Generated! Template link copied to clipboard. Share it with your team!',
            severity: 'success'
        });
    };

    // Save as template
    const saveAsTemplate = () => {
        setLocalSnackbar({
            open: true,
            message: '💾 Template Saved! Your custom signature template has been saved to your library.',
            severity: 'success'
        });
    };

    // AI suggestions
    const handleAiSuggestions = () => {
        if (!aiSignatureEnabled || isTrial) {
            setLocalSnackbar({
                open: true,
                message: isTrial ? '⭐ Upgrade Required! AI features are available in the premium version.' : '🤖 AI features are not enabled for your organization.',
                severity: 'info'
            });
            return;
        }

        setLocalSnackbar({
            open: true,
            message: '🧠 Analyzing your profile...',
            severity: 'info'
        });

        // Simulate AI suggestions
        setTimeout(() => {
            const industry = profileData.title?.toLowerCase() || '';
            let suggestedColor = '#2563eb';

            if (industry.includes('design') || industry.includes('creative')) {
                suggestedColor = '#EC4899';
            } else if (industry.includes('finance') || industry.includes('banking')) {
                suggestedColor = '#10B981';
            } else if (industry.includes('tech') || industry.includes('engineer')) {
                suggestedColor = '#8B5CF6';
            }

            setSignatureColor(suggestedColor);
            setLocalSnackbar({
                open: true,
                message: '✨ AI Magic Applied! Your signature has been optimized based on your industry and role. The color scheme and layout have been updated for maximum professional impact!',
                severity: 'success'
            });
        }, 2000);
    };

    // Apply template with notification
    const applyTemplate = (template) => {
        setSignatureTheme(template.theme);
        setSignatureColor(template.color);
        setSignatureLayout(template.layout);
        setFontFamily(template.fontFamily || 'Inter');
        setBorderRadius(template.borderRadius || 8);
        
        setLocalSnackbar({
            open: true,
            message: `🎨 "${template.name}" Template Applied! Your signature has been updated with the ${template.name.toLowerCase()} style.`,
            severity: 'success'
        });
    };

    return (
        <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: 3 }}>
            <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
                {/* Header */}
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    {/* <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                        ✨ AI-Powered Email Signature Generator
                    </Typography> */}
                    <Typography variant="h6" sx={{ color: '#64748b', mb: 3 }}>
                        Create professional signatures that make lasting impressions
                    </Typography>
                    
                    {/* Quick Actions */}
                    {/* <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                        <Button
                            variant="contained"
                            startIcon={<Wand2 />}
                            onClick={handleAiSuggestions}
                            disabled={isTrial}
                            sx={{ 
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                px: 3
                            }}
                        >
                            AI Suggestions
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshCw />}
                            onClick={() => window.location.reload()}
                        >
                            Reset All
                        </Button>
                    </Stack> */}

                    {/* Feature badges */}
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                        {/* <Chip icon={<Sparkles size={16} />} label="AI-Powered" color="primary" variant="outlined" /> */}
                        <Chip icon={<Eye size={16} />} label="Live Preview" color="success" variant="outlined" />
                        <Chip icon={<Smartphone size={16} />} label="Mobile Ready" color="info" variant="outlined" />
                        <Chip icon={<Zap size={16} />} label="Quick Export" color="warning" variant="outlined" />
                    </Stack>
                </Box>

                {/* AI Banner */}
                {aiSignatureEnabled && !aiSuggestions && (
                    <Alert
                        severity="info"
                        sx={{ 
                            mb: 3, 
                            background: 'linear-gradient(135deg, #667eea22 0%, #764ba222 100%)',
                            border: '1px solid #667eea33'
                        }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleAiSuggestions}
                                disabled={isTrial}
                                startIcon={<Star />}
                            >
                                Apply AI Suggestions
                            </Button>
                        }
                    >
                        <AlertTitle>🚀 AI Recommendations Available</AlertTitle>
                        Let AI suggest the perfect signature style based on your industry and role
                        {isTrial && (
                            <Box mt={1}>
                                <Typography variant="caption" fontWeight="bold" color="warning.main">
                                    ⭐ Upgrade to unlock AI features
                                </Typography>
                            </Box>
                        )}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* Settings Panel */}
                    <Grid item xs={12} lg={4}>
                        <Card sx={{ 
                            height: 'fit-content',
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                            borderRadius: 3
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Settings size={24} style={{ marginRight: 8 }} />
                                    <Typography variant="h6" fontWeight="bold">
                                        Signature Settings
                                    </Typography>
                                </Box>

                                {/* Settings Tabs */}
                                <Tabs
                                    value={activeTab}
                                    onChange={(e, newValue) => setActiveTab(newValue)}
                                    variant="fullWidth"
                                    sx={{ mb: 3 }}
                                >
                                    <Tab label="Design" icon={<Palette size={16} />} />
                                    <Tab label="Content" icon={<User size={16} />} />
                                    <Tab label="Advanced" icon={<Settings size={16} />} />
                                </Tabs>

                                {/* Design Tab */}
                                {activeTab === 0 && (
                                    <Stack spacing={3}>
                                        {/* Quick Templates */}
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                ⚡ Quick Templates
                                            </Typography>
                                            <Grid container spacing={1}>
                                                {templates.map((template, index) => (
                                                    <Grid item xs={6} key={index}>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            onClick={() => applyTemplate(template)}
                                                            sx={{
                                                                borderColor: template.color,
                                                                color: template.color,
                                                                '&:hover': {
                                                                    borderColor: template.color,
                                                                    backgroundColor: `${template.color}11`
                                                                }
                                                            }}
                                                        >
                                                            {template.name}
                                                        </Button>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>

                                        {/* Theme Selection */}
                                        <Accordion expanded={expandedSections.includes('theme')} onChange={() => toggleSection('theme')}>
                                            <AccordionSummary expandIcon={<ChevronDown />}>
                                                <Typography fontWeight="bold">🎨 Theme & Style</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    <FormControl>
                                                        <FormLabel>Theme Style</FormLabel>
                                                        <RadioGroup
                                                            value={signatureTheme}
                                                            onChange={(e) => setSignatureTheme(e.target.value)}
                                                            row
                                                        >
                                                            <FormControlLabel value="modern" control={<Radio />} label="Modern" />
                                                            <FormControlLabel value="classic" control={<Radio />} label="Classic" />
                                                            <FormControlLabel value="minimal" control={<Radio />} label="Minimal" />
                                                        </RadioGroup>
                                                    </FormControl>

                                                    <FormControl>
                                                        <FormLabel>Layout</FormLabel>
                                                        <RadioGroup
                                                            value={signatureLayout}
                                                            onChange={(e) => setSignatureLayout(e.target.value)}
                                                            row
                                                        >
                                                            <FormControlLabel value="horizontal" control={<Radio />} label="Horizontal" />
                                                            <FormControlLabel value="vertical" control={<Radio />} label="Vertical" />
                                                        </RadioGroup>
                                                    </FormControl>

                                                    <FormControl fullWidth>
                                                        <InputLabel>Font Family</InputLabel>
                                                        <Select
                                                            value={fontFamily}
                                                            onChange={(e) => setFontFamily(e.target.value)}
                                                            label="Font Family"
                                                        >
                                                            {fontOptions.map((font) => (
                                                                <MenuItem key={font.name} value={font.name}>
                                                                    {font.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        {/* Color Palette */}
                                        <Accordion expanded={expandedSections.includes('colors')} onChange={() => toggleSection('colors')}>
                                            <AccordionSummary expandIcon={<ChevronDown />}>
                                                <Typography fontWeight="bold">🌈 Colors & Effects</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    <Box>
                                                        <Typography variant="caption" display="block" gutterBottom>
                                                            Color Palettes
                                                        </Typography>
                                                        {colorPalettes.map((palette, index) => (
                                                            <Box key={index} sx={{ mb: 1 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {palette.name}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                                                    {palette.colors.map((color) => (
                                                                        <Box
                                                                            key={color}
                                                                            onClick={() => handleColorSelect(color)}
                                                                            sx={{
                                                                                width: 28,
                                                                                height: 28,
                                                                                borderRadius: '50%',
                                                                                bgcolor: color,
                                                                                cursor: 'pointer',
                                                                                border: signatureColor === color ? '3px solid #000' : '2px solid transparent',
                                                                                transition: 'all 0.2s',
                                                                                '&:hover': { transform: 'scale(1.1)' }
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>

                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={signatureGradient}
                                                                onChange={(e) => setSignatureGradient(e.target.checked)}
                                                            />
                                                        }
                                                        label="Enable Gradient Effects"
                                                    />
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        {/* Typography */}
                                        <Accordion expanded={expandedSections.includes('typography')} onChange={() => toggleSection('typography')}>
                                            <AccordionSummary expandIcon={<ChevronDown />}>
                                                <Typography fontWeight="bold">📝 Typography</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    <Box>
                                                        <Typography variant="caption" gutterBottom>
                                                            Font Size: {fontSize}px
                                                        </Typography>
                                                        <Slider
                                                            value={fontSize}
                                                            onChange={(e, value) => setFontSize(value)}
                                                            min={10}
                                                            max={20}
                                                            step={1}
                                                            marks
                                                        />
                                                    </Box>

                                                    <Box>
                                                        <Typography variant="caption" gutterBottom>
                                                            Border Radius: {borderRadius}px
                                                        </Typography>
                                                        <Slider
                                                            value={borderRadius}
                                                            onChange={(e, value) => setBorderRadius(value)}
                                                            min={0}
                                                            max={20}
                                                            step={1}
                                                        />
                                                    </Box>

                                                    <Box>
                                                        <Typography variant="caption" gutterBottom>
                                                            Padding: {padding}px
                                                        </Typography>
                                                        <Slider
                                                            value={padding}
                                                            onChange={(e, value) => setPadding(value)}
                                                            min={8}
                                                            max={32}
                                                            step={2}
                                                        />
                                                    </Box>

                                                    <Box>
                                                        <Typography variant="caption" gutterBottom>
                                                            Shadow Intensity: {shadowIntensity}
                                                        </Typography>
                                                        <Slider
                                                            value={shadowIntensity}
                                                            onChange={(e, value) => setShadowIntensity(value)}
                                                            min={0}
                                                            max={5}
                                                            step={1}
                                                        />
                                                    </Box>
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Stack>
                                )}

                                {/* Content Tab */}
                                {activeTab === 1 && (
                                    <Stack spacing={3}>
                                        {/* Personal Info */}
                                        <Accordion expanded={expandedSections.includes('personal')} onChange={() => toggleSection('personal')}>
                                            <AccordionSummary expandIcon={<ChevronDown />}>
                                                <Typography fontWeight="bold">👤 Personal Info</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={showPronouns}
                                                                onChange={(e) => setShowPronouns(e.target.checked)}
                                                            />
                                                        }
                                                        label="Show Pronouns"
                                                    />

                                                    {showPronouns && (
                                                        <FormControl>
                                                            <RadioGroup
                                                                value={pronounChoice}
                                                                onChange={(e) => setPronounChoice(e.target.value)}
                                                            >
                                                                <FormControlLabel value="he/him" control={<Radio />} label="He/Him" />
                                                                <FormControlLabel value="she/her" control={<Radio />} label="She/Her" />
                                                                <FormControlLabel value="they/them" control={<Radio />} label="They/Them" />
                                                                <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                                                            </RadioGroup>
                                                            {pronounChoice === 'custom' && (
                                                                <TextField
                                                                    size="small"
                                                                    placeholder="Enter pronouns"
                                                                    value={customPronoun}
                                                                    onChange={(e) => setCustomPronoun(e.target.value)}
                                                                />
                                                            )}
                                                        </FormControl>
                                                    )}

                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={showNickname}
                                                                onChange={(e) => setShowNickname(e.target.checked)}
                                                            />
                                                        }
                                                        label="Show Nickname"
                                                    />

                                                    {showNickname && (
                                                        <TextField
                                                            size="small"
                                                            label="Nickname"
                                                            value={nickname}
                                                            onChange={(e) => setNickname(e.target.value)}
                                                            placeholder="Preferred name"
                                                        />
                                                    )}

                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={includeTagline}
                                                                onChange={(e) => setIncludeTagline(e.target.checked)}
                                                            />
                                                        }
                                                        label="Include Tagline"
                                                    />

                                                    {includeTagline && (
                                                        <TextField
                                                            size="small"
                                                            label="Personal Tagline"
                                                            value={tagline}
                                                            onChange={(e) => setTagline(e.target.value)}
                                                            placeholder="Your professional motto"
                                                            multiline
                                                            rows={2}
                                                        />
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        {/* Photo Settings */}
                                        <Accordion expanded={expandedSections.includes('photo')} onChange={() => toggleSection('photo')}>
                                            <AccordionSummary expandIcon={<ChevronDown />}>
                                                <Typography fontWeight="bold">📸 Photo & QR</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={includePhoto}
                                                                onChange={(e) => setIncludePhoto(e.target.checked)}
                                                            />
                                                        }
                                                        label="Include Photo"
                                                    />

                                                    {includePhoto && (
                                                        <>
                                                            <FormControl>
                                                                <RadioGroup
                                                                    value={photoType}
                                                                    onChange={(e) => setPhotoType(e.target.value)}
                                                                    row
                                                                >
                                                                    <FormControlLabel value="profile" control={<Radio />} label="Profile" />
                                                                    <FormControlLabel value="company" control={<Radio />} label="Logo" />
                                                                </RadioGroup>
                                                            </FormControl>

                                                            <FormControl>
                                                                <FormLabel>Photo Shape</FormLabel>
                                                                <RadioGroup
                                                                    value={photoShape}
                                                                    onChange={(e) => setPhotoShape(e.target.value)}
                                                                    row
                                                                >
                                                                    <FormControlLabel value="circle" control={<Radio />} label="Circle" />
                                                                    <FormControlLabel value="rounded" control={<Radio />} label="Rounded" />
                                                                    <FormControlLabel value="square" control={<Radio />} label="Square" />
                                                                </RadioGroup>
                                                            </FormControl>

                                                            <Box>
                                                                <Typography variant="caption" gutterBottom>
                                                                    Photo Size: {photoSize}px
                                                                </Typography>
                                                                <Slider
                                                                    value={photoSize}
                                                                    onChange={(e, value) => setPhotoSize(value)}
                                                                    min={50}
                                                                    max={120}
                                                                    step={5}
                                                                />
                                                            </Box>
                                                        </>
                                                    )}

                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={includeQR}
                                                                onChange={(e) => setIncludeQR(e.target.checked)}
                                                            />
                                                        }
                                                        label="Include QR Code"
                                                    />

                                                    {includeQR && (
                                                        <>
                                                            <FormControl>
                                                                <RadioGroup
                                                                    value={qrCodeType}
                                                                    onChange={(e) => setQrCodeType(e.target.value)}
                                                                >
                                                                    <FormControlLabel value="profile" control={<Radio />} label="Profile Link" />
                                                                    <FormControlLabel value="contact" control={<Radio />} label="Contact Card" />
                                                                </RadioGroup>
                                                            </FormControl>

                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        checked={includeQRPhoto}
                                                                        onChange={(e) => setIncludeQRPhoto(e.target.checked)}
                                                                    />
                                                                }
                                                                label="Add photo to QR center"
                                                            />

                                                            <Box>
                                                                <Typography variant="caption" gutterBottom>
                                                                    QR Size: {qrSize}px
                                                                </Typography>
                                                                <Slider
                                                                    value={qrSize}
                                                                    onChange={(e, value) => setQrSize(value)}
                                                                    min={50}
                                                                    max={100}
                                                                    step={5}
                                                                />
                                                            </Box>
                                                        </>
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        {/* Social Media */}
                                        <Accordion expanded={expandedSections.includes('social')} onChange={() => toggleSection('social')}>
                                            <AccordionSummary expandIcon={<ChevronDown />}>
                                                <Typography fontWeight="bold">🌐 Social Media</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    {[
                                                        { name: 'LinkedIn', state: includeLinkedin, setState: setIncludeLinkedin, url: linkedinUrl, setUrl: setLinkedinUrl, icon: Linkedin },
                                                        { name: 'Twitter', state: includeTwitter, setState: setIncludeTwitter, url: twitterUrl, setUrl: setTwitterUrl, icon: Twitter },
                                                        { name: 'Instagram', state: includeInstagram, setState: setIncludeInstagram, url: instagramUrl, setUrl: setInstagramUrl, icon: Instagram },
                                                        { name: 'GitHub', state: includeGithub, setState: setIncludeGithub, url: githubUrl, setUrl: setGithubUrl, icon: Github },
                                                        { name: 'YouTube', state: includeYouTube, setState: setIncludeYouTube, url: youtubeUrl, setUrl: setYoutubeUrl, icon: Youtube }
                                                    ].map((social) => (
                                                        <Box key={social.name}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        checked={social.state}
                                                                        onChange={(e) => social.setState(e.target.checked)}
                                                                    />
                                                                }
                                                                label={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <social.icon size={16} />
                                                                        {social.name}
                                                                    </Box>
                                                                }
                                                            />
                                                            {social.state && (
                                                                <TextField
                                                                    size="small"
                                                                    fullWidth
                                                                    value={social.url}
                                                                    onChange={(e) => social.setUrl(e.target.value)}
                                                                    placeholder={`Your ${social.name} URL`}
                                                                    sx={{ mt: 1 }}
                                                                />
                                                            )}
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Stack>
                                )}

                                {/* Advanced Tab */}
                                {activeTab === 2 && (
                                    <Stack spacing={3}>
                                        {/* Meeting Scheduler */}
                                        <Accordion expanded={expandedSections.includes('meeting')} onChange={() => toggleSection('meeting')}>
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Typography fontWeight="bold">📅 Meeting Scheduler</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={includeMeetingScheduler}
                                                                onChange={(e) => setIncludeMeetingScheduler(e.target.checked)}
                                                            />
                                                        }
                                                        label="Include Meeting Scheduler"
                                                    />

                                                    {includeMeetingScheduler && (
                                                        <>
                                                            <TextField
                                                                size="small"
                                                                label="Scheduler URL"
                                                                value={meetingSchedulerUrl}
                                                                onChange={(e) => setMeetingSchedulerUrl(e.target.value)}
                                                                placeholder="https://calendly.com/your-profile"
                                                            />

                                                            <FormControl>
                                                                <FormLabel>Button Style</FormLabel>
                                                                <RadioGroup
                                                                    value={meetingButtonStyle}
                                                                    onChange={(e) => setMeetingButtonStyle(e.target.value)}
                                                                    row
                                                                >
                                                                    <FormControlLabel value="button" control={<Radio />} label="Button" />
                                                                    <FormControlLabel value="link" control={<Radio />} label="Link" />
                                                                    <FormControlLabel value="badge" control={<Radio />} label="Badge" />
                                                                </RadioGroup>
                                                            </FormControl>
                                                        </>
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        {/* Banner */}
                                        <Accordion expanded={expandedSections.includes('banner')} onChange={() => toggleSection('banner')}>
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Typography fontWeight="bold">📢 Promotional Banner</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={includeBanner}
                                                                onChange={(e) => setIncludeBanner(e.target.checked)}
                                                            />
                                                        }
                                                        label="Include Promotional Banner"
                                                    />

                                                    {includeBanner && (
                                                        <>
                                                            <TextField
                                                                size="small"
                                                                label="Banner Text"
                                                                value={bannerText}
                                                                onChange={(e) => setBannerText(e.target.value)}
                                                                multiline
                                                                rows={2}
                                                            />

                                                            <TextField
                                                                size="small"
                                                                label="Banner Link (optional)"
                                                                value={bannerUrl}
                                                                onChange={(e) => setBannerUrl(e.target.value)}
                                                                placeholder="https://example.com"
                                                            />
                                                        </>
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        {/* Legal Disclaimer */}
                                        <Accordion expanded={expandedSections.includes('legal')} onChange={() => toggleSection('legal')}>
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Typography fontWeight="bold">⚖️ Legal Disclaimer</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={includeDisclaimer}
                                                                onChange={(e) => setIncludeDisclaimer(e.target.checked)}
                                                            />
                                                        }
                                                        label="Include Legal Disclaimer"
                                                    />

                                                    {includeDisclaimer && (
                                                        <TextField
                                                            size="small"
                                                            label="Disclaimer Text"
                                                            value={disclaimerText}
                                                            onChange={(e) => setDisclaimerText(e.target.value)}
                                                            multiline
                                                            rows={3}
                                                        />
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>

                                        {/* Analytics */}
                                        <Accordion expanded={expandedSections.includes('analytics')} onChange={() => toggleSection('analytics')}>
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Typography fontWeight="bold">📊 Analytics</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={analyticsEnabled}
                                                                onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                                                                disabled={isTrial}
                                                            />
                                                        }
                                                        label="Enable Click Tracking"
                                                    />

                                                    <Typography variant="caption" color="text.secondary">
                                                        Track clicks on your signature links and QR codes
                                                        {isTrial && " (Premium feature)"}
                                                    </Typography>

                                                    {analyticsEnabled && !isTrial && (
                                                        <Alert severity="info" size="small">
                                                            <Typography variant="caption">
                                                                Analytics data will be available in your dashboard
                                                            </Typography>
                                                        </Alert>
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Preview Panel */}
                    <Grid item xs={12} lg={8}>
                        <Card sx={{ 
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                            borderRadius: 3,
                            height: 'fit-content'
                        }}>
                            <CardContent sx={{ p: 3 }}>
                                {/* Preview Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Eye size={24} />
                                        <Typography variant="h6" fontWeight="bold">
                                            Live Preview
                                        </Typography>
                                        <Chip label="Live" color="success" size="small" />
                                    </Box>

                                    {/* Preview Controls */}
                                    <Stack direction="row" spacing={1}>
                                        <ButtonGroup size="small" variant="outlined">
                                            <Button
                                                onClick={() => setPreviewDevice('desktop')}
                                                variant={previewDevice === 'desktop' ? 'contained' : 'outlined'}
                                                startIcon={<Monitor size={16} />}
                                            >
                                                Desktop
                                            </Button>
                                            <Button
                                                onClick={() => setPreviewDevice('tablet')}
                                                variant={previewDevice === 'tablet' ? 'contained' : 'outlined'}
                                                startIcon={<Tablet size={16} />}
                                            >
                                                Tablet
                                            </Button>
                                            <Button
                                                onClick={() => setPreviewDevice('mobile')}
                                                variant={previewDevice === 'mobile' ? 'contained' : 'outlined'}
                                                startIcon={<Smartphone size={16} />}
                                            >
                                                Mobile
                                            </Button>
                                        </ButtonGroup>

                                        <ButtonGroup size="small" variant="outlined">
                                            <Button
                                                onClick={() => setPreviewScale(Math.max(0.5, previewScale - 0.1))}
                                                disabled={previewScale <= 0.5}
                                            >
                                                <ZoomOut size={16} />
                                            </Button>
                                            <Button>
                                                {Math.round(previewScale * 100)}%
                                            </Button>
                                            <Button
                                                onClick={() => setPreviewScale(Math.min(1.5, previewScale + 0.1))}
                                                disabled={previewScale >= 1.5}
                                            >
                                                <ZoomIn size={16} />
                                            </Button>
                                        </ButtonGroup>
                                    </Stack>
                                </Box>

                                {/* Preview Container */}
                                <Paper
                                    sx={{
                                        p: 3,
                                        mb: 3,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        position: 'relative',
                                        overflow: 'auto',
                                        maxWidth: previewDevice === 'mobile' ? '400px' : 
                                                 previewDevice === 'tablet' ? '600px' : '100%',
                                        mx: previewDevice !== 'desktop' ? 'auto' : 0,
                                        background: showGridlines ? 
                                            'linear-gradient(90deg, #f0f0f0 1px, transparent 1px), linear-gradient(180deg, #f0f0f0 1px, transparent 1px)' : 
                                            '#ffffff',
                                        backgroundSize: showGridlines ? '20px 20px' : 'auto'
                                    }}
                                >
                                    {/* Trial Overlay */}
                                    {isTrial && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 10,
                                                backdropFilter: 'blur(2px)',
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Paper
                                                sx={{
                                                    p: 4,
                                                    textAlign: 'center',
                                                    maxWidth: '400px',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white'
                                                }}
                                            >
                                                <Star size={48} style={{ marginBottom: 16 }} />
                                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                                    Premium Feature
                                                </Typography>
                                                <Typography variant="body2" paragraph>
                                                    Unlock the full signature generator with advanced customization, AI suggestions, and export features.
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="inherit"
                                                    size="large"
                                                    onClick={() => navigate('/profiles/upgrade')}
                                                    sx={{ 
                                                        background: 'rgba(255,255,255,0.2)',
                                                        backdropFilter: 'blur(10px)',
                                                        '&:hover': {
                                                            background: 'rgba(255,255,255,0.3)'
                                                        }
                                                    }}
                                                >
                                                    Upgrade Now
                                                </Button>
                                            </Paper>
                                        </Box>
                                    )}

                                    {/* Signature */}
                                    <Box
                                        ref={signatureRef}
                                        sx={{
                                            transform: `scale(${previewScale})`,
                                            transformOrigin: 'top left',
                                            transition: 'transform 0.3s ease'
                                        }}
                                    >
                                        <Box sx={{ ...getSignatureStyles().container }}>
                                            {/* Avatar and QR section */}
                                            {(includePhoto || includeQR) && (
                                                <Box sx={{ ...getSignatureStyles().avatarContainer }}>
                                                    {includePhoto && (
                                                        <img
                                                            src={getPhotoSource(photoType)}
                                                            alt={photoType === 'profile' ? profileData.name : profileData.company}
                                                            style={{ ...getSignatureStyles().avatar }}
                                                        />
                                                    )}

                                                    {includeQR && (
                                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                            <QRCodeSVG
                                                                value={getQRCodeValue()}
                                                                size={qrSize * previewScale}
                                                                level={includeQRPhoto ? "H" : "M"}
                                                                imageSettings={includeQRPhoto ? {
                                                                    src: getPhotoSource(qrPhotoType),
                                                                    height: 24,
                                                                    width: 24,
                                                                    excavate: true
                                                                } : undefined}
                                                            />
                                                            {qrCodeType === 'contact' && (
                                                                <Box sx={{
                                                                    fontSize: `${10 * previewScale}px`,
                                                                    mt: 0.5,
                                                                    textAlign: 'center',
                                                                    color: signatureColor
                                                                }}>
                                                                    Scan for contact
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}

                                            {/* Content section */}
                                            <Box>
                                                {/* Banner */}
                                                {includeBanner && (
                                                    <Box sx={{
                                                        mb: 2,
                                                        p: 1,
                                                        backgroundColor: `${signatureColor}22`,
                                                        borderRadius: 1,
                                                        border: `1px solid ${signatureColor}44`
                                                    }}>
                                                        <Typography variant="caption" sx={{ 
                                                            color: signatureColor,
                                                            fontWeight: 'bold',
                                                            fontSize: `${12 * previewScale}px`
                                                        }}>
                                                            {bannerText}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Regards */}
                                                <Box sx={{ mb: 1, fontSize: `${fontSize * previewScale}px` }}>
                                                    Regards,
                                                </Box>

                                                {/* Name and details */}
                                                <Box sx={{ ...getSignatureStyles().name }}>
                                                    {profileData.name}
                                                    {showNickname && nickname && (
                                                        <Box component="span" sx={{ fontWeight: 'normal', fontSize: '0.8em', ml: 1 }}>
                                                            ({nickname})
                                                        </Box>
                                                    )}
                                                    {showPronouns && (
                                                        <Box component="span" sx={{
                                                            fontWeight: 'normal',
                                                            fontSize: '0.8em',
                                                            ml: 1,
                                                            color: 'inherit'
                                                        }}>
                                                            ({pronounChoice === 'custom' ? customPronoun : pronounChoice})
                                                        </Box>
                                                    )}
                                                </Box>

                                                <Box sx={{ ...getSignatureStyles().title }}>
                                                    {profileData.title}
                                                </Box>

                                                <Box sx={{ ...getSignatureStyles().company }}>
                                                    {profileData.company}
                                                </Box>

                                                {/* Tagline */}
                                                {includeTagline && tagline && (
                                                    <Box sx={{
                                                        fontSize: `${(fontSize - 2) * previewScale}px`,
                                                        fontStyle: 'italic',
                                                        color: signatureColor,
                                                        margin: '4px 0 8px 0'
                                                    }}>
                                                        {tagline}
                                                    </Box>
                                                )}

                                                {/* Contact info */}
                                                <Box sx={{ ...getSignatureStyles().contact }}>
                                                    <Box sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        ...(signatureTheme === 'modern' ? getSignatureStyles().icon : {})
                                                    }}>
                                                        <Phone
                                                            size={14 * previewScale}
                                                            color={signatureTheme === 'modern' ? '#fff' : signatureColor}
                                                        />
                                                    </Box>
                                                    {profileData.phone}
                                                </Box>

                                                <Box sx={{ ...getSignatureStyles().contact }}>
                                                    <Box sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        ...(signatureTheme === 'modern' ? getSignatureStyles().icon : {})
                                                    }}>
                                                        <Mail
                                                            size={14 * previewScale}
                                                            color={signatureTheme === 'modern' ? '#fff' : signatureColor}
                                                        />
                                                    </Box>
                                                    {profileData.email}
                                                </Box>

                                                <Box sx={{ ...getSignatureStyles().contact }}>
                                                    <Box sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        ...(signatureTheme === 'modern' ? getSignatureStyles().icon : {})
                                                    }}>
                                                        <Globe
                                                            size={14 * previewScale}
                                                            color={signatureTheme === 'modern' ? '#fff' : signatureColor}
                                                        />
                                                    </Box>
                                                    {profileData.companyUrl}
                                                </Box>

                                                {/* LinkedIn as a contact line, like email/website (the social row below is icon-only) */}
                                                {includeLinkedin && linkedinUrl && (
                                                    <Box sx={{ ...getSignatureStyles().contact }}>
                                                        <Box sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            ...(signatureTheme === 'modern' ? getSignatureStyles().icon : {})
                                                        }}>
                                                            <Linkedin
                                                                size={14 * previewScale}
                                                                color={signatureTheme === 'modern' ? '#fff' : signatureColor}
                                                            />
                                                        </Box>
                                                        <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                                                            {linkedinUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                                                        </a>
                                                    </Box>
                                                )}

                                                {/* Social Media Links */}
                                                <Box sx={{ ...getSignatureStyles().socialContainer }}>
                                                    {/* LinkedIn is shown as its own contact line above */}
                                                    {includeTwitter && (
                                                        <a href={twitterUrl} style={{ ...getSignatureStyles().socialIcon }}>
                                                            <Twitter size={14 * previewScale} color={signatureTheme === 'modern' ? '#fff' : signatureColor} />
                                                        </a>
                                                    )}
                                                    {includeInstagram && (
                                                        <a href={instagramUrl} style={{ ...getSignatureStyles().socialIcon }}>
                                                            <Instagram size={14 * previewScale} color={signatureTheme === 'modern' ? '#fff' : signatureColor} />
                                                        </a>
                                                    )}
                                                    {includeGithub && (
                                                        <a href={githubUrl} style={{ ...getSignatureStyles().socialIcon }}>
                                                            <Github size={14 * previewScale} color={signatureTheme === 'modern' ? '#fff' : signatureColor} />
                                                        </a>
                                                    )}
                                                    {includeYouTube && (
                                                        <a href={youtubeUrl} style={{ ...getSignatureStyles().socialIcon }}>
                                                            <Youtube size={14 * previewScale} color={signatureTheme === 'modern' ? '#fff' : signatureColor} />
                                                        </a>
                                                    )}
                                                </Box>

                                                {/* Meeting Scheduler */}
                                                {includeMeetingScheduler && meetingSchedulerUrl && (
                                                    <Box sx={{ mt: 2 }}>
                                                        {meetingButtonStyle === 'button' && (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<Calendar size={14} />}
                                                                sx={{
                                                                    backgroundColor: signatureColor,
                                                                    fontSize: `${12 * previewScale}px`,
                                                                    textTransform: 'none'
                                                                }}
                                                                href={meetingSchedulerUrl}
                                                            >
                                                                Schedule Meeting
                                                            </Button>
                                                        )}
                                                        {meetingButtonStyle === 'link' && (
                                                            <Box sx={{ ...getSignatureStyles().contact }}>
                                                                <Calendar size={14 * previewScale} color={signatureColor} />
                                                                <a href={meetingSchedulerUrl} style={{ color: signatureColor }}>
                                                                    Schedule a Meeting
                                                                </a>
                                                            </Box>
                                                        )}
                                                        {meetingButtonStyle === 'badge' && (
                                                            <Chip
                                                                icon={<Calendar size={14} />}
                                                                label="Schedule Meeting"
                                                                size="small"
                                                                clickable
                                                                sx={{
                                                                    backgroundColor: signatureColor,
                                                                    color: 'white',
                                                                    fontSize: `${11 * previewScale}px`
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}

                                                {/* Disclaimer */}
                                                {includeDisclaimer && (
                                                    <Box sx={{
                                                        mt: 2,
                                                        pt: 1,
                                                        borderTop: '1px solid #e0e0e0',
                                                        fontSize: `${10 * previewScale}px`,
                                                        color: '#666666',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        {disclaimerText}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Paper>

                                {/* Action Buttons */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Tooltip 
                                            title="Copies signature with formatting, images, and links intact - paste directly into your email client"
                                            arrow
                                            placement="top"
                                        >
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                startIcon={<Copy />}
                                                onClick={copySignatureRichContent}
                                                disabled={isTrial}
                                                sx={{
                                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                                    }
                                                }}
                                            >
                                                {isTrial ? "Upgrade to Copy" : "Copy for Email"}
                                            </Button>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Tooltip 
                                            title="Copies raw HTML code - for developers or custom implementations"
                                            arrow
                                            placement="top"
                                        >
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                startIcon={<Code />}
                                                onClick={copySignatureHTML}
                                                disabled={isTrial}
                                            >
                                                {isTrial ? "Upgrade for HTML" : "Copy HTML Code"}
                                            </Button>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Tooltip 
                                            title="Downloads signature as high-quality PNG image"
                                            arrow
                                            placement="top"
                                        >
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                startIcon={<Download />}
                                                onClick={downloadSignatureImage}
                                                disabled={isTrial}
                                            >
                                                {isTrial ? "Upgrade to Download" : "Download PNG"}
                                            </Button>
                                        </Tooltip>
                                    </Grid>
                                </Grid>

                                {/* Secondary Actions */}
                                {/* <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Button
                                            variant="text"
                                            fullWidth
                                            startIcon={<Share />}
                                            onClick={shareTemplate}
                                            disabled={isTrial}
                                            size="small"
                                        >
                                            {isTrial ? "Upgrade to Share" : "Share Template"}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Button
                                            variant="text"
                                            fullWidth
                                            startIcon={<Save />}
                                            onClick={saveAsTemplate}
                                            disabled={isTrial}
                                            size="small"
                                        >
                                            {isTrial ? "Upgrade to Save" : "Save as Template"}
                                        </Button>
                                    </Grid>
                                </Grid> */}

                                {/* Copy Instructions */}
                                {!isTrial && (
                                    <Alert 
                                        severity="info" 
                                        sx={{ 
                                            mt: 2,
                                            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                            border: '1px solid #93c5fd'
                                        }}
                                    >
                                        <AlertTitle>📋 How to Use Your Signature</AlertTitle>
                                        <Typography variant="body2" component="div">
                                            <strong>For most email clients:</strong> Click "Copy for Email" then paste (Ctrl+V) directly into your email signature settings.
                                            <br />
                                            <strong>For advanced users:</strong> Use "Copy HTML Code" if you need the raw HTML for custom implementations.
                                        </Typography>
                                    </Alert>
                                )}

                                {/* Stats */}
                                {/* {!isTrial && (
                                    <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                            📊 Signature Stats
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6" fontWeight="bold" color="primary">
                                                        {Math.round(signatureColor.length * 42)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Estimated Views
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6" fontWeight="bold" color="success.main">
                                                        94%
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Professional Score
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6" fontWeight="bold" color="warning.main">
                                                        {Object.values({ includeLinkedin, includeTwitter, includeInstagram, includeGithub, includeYouTube }).filter(Boolean).length}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Social Links
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="h6" fontWeight="bold" color="info.main">
                                                        A+
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Email Grade
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )} */}

                                {/* Trial Alert */}
                                {isTrial && (
                                    <Alert
                                        severity="info"
                                        sx={{ 
                                            mt: 3,
                                            background: 'linear-gradient(135deg, #667eea22 0%, #764ba222 100%)',
                                            border: '1px solid #667eea33'
                                        }}
                                        action={
                                            <Button
                                                color="inherit"
                                                size="small"
                                                onClick={() => navigate('/profiles/upgrade')}
                                                startIcon={<TrendingUp />}
                                            >
                                                Upgrade Now
                                            </Button>
                                        }
                                    >
                                        <AlertTitle>🎯 Trial Mode Active</AlertTitle>
                                        You're using the signature generator in trial mode. Upgrade to unlock all features including AI suggestions, advanced customization, analytics, and export options.
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Enhanced Snackbar Notifications */}
            <Snackbar
                open={localSnackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                TransitionComponent={Slide}
                TransitionProps={{ direction: 'up' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={localSnackbar.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        maxWidth: '420px',
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        '& .MuiAlert-icon': {
                            fontSize: '1.5rem'
                        },
                        '& .MuiAlert-message': {
                            fontSize: '0.95rem',
                            lineHeight: 1.4
                        },
                        '& .MuiAlert-action': {
                            paddingTop: 0
                        }
                    }}
                    iconMapping={{
                        success: <CheckCircle size={24} />,
                        error: <AlertCircle size={24} />,
                        warning: <AlertCircle size={24} />,
                        info: <Info size={24} />
                    }}
                    action={
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={handleCloseSnackbar}
                            sx={{ 
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            <X size={18} />
                        </IconButton>
                    }
                >
                    {localSnackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EmailSignatureGenerator;